import { apiClient } from 'lib/api';
import { useQuery } from '@tanstack/react-query';
import { isAxiosError } from 'axios';
import type { SynchronousQueryConfig } from 'lib/react-query';
import queryKeys from './queryKeys';

export interface AdSettingsRequestAdSet {
  account_id: string;
  name: string;
  id: string;
  campaign_id: string;
  flow: string;
}

export interface AdSettingsRequestBody {
  app: string;
  platform: string;
  flows: string[];
}

// Partnership ad settings still expect the full ad sets, not just their flows.
export interface PartnershipAdSettingsRequestBody {
  app: string;
  platform: string;
  ad_sets: AdSettingsRequestAdSet[];
  app_page_id?: string;
}

type AdSettingsRequestPayload =
  | AdSettingsRequestBody
  | PartnershipAdSettingsRequestBody;

export interface AdSettingPageEntry {
  name: string;
  is_default: boolean;
  id: number;
}

export interface AdSettingTextEntry {
  name: string;
  is_default: boolean;
  id: number;
}

export interface AdSettingEntry {
  pages: AdSettingPageEntry[];
  texts: AdSettingTextEntry[];
}

export interface AdSettingEntryWithName extends AdSettingEntry {
  name: string;
}

export type AdSettingsResponse = Record<string, AdSettingEntry>;

export const getAdSettings = async (body: AdSettingsRequestPayload, url: string): Promise<AdSettingsResponse> => {
  const res = await apiClient.post<AdSettingsResponse>(
    url,
    body,
  );

  if (isAxiosError(res)) {
    const error = new Error('Get Ad Settings Failed');
    error.cause = res.response?.data;
    return Promise.reject(error);
  }
  return res.data;
};

interface UseAdSettings<TData> {
  config?: SynchronousQueryConfig<typeof getAdSettings, TData>;
  params: AdSettingsRequestPayload;
  isPartnershipAds?: boolean;
  appPageId?: string;
}

const useAdSettings = <TData = AdSettingsResponse>({
  params: body,
  config,
  isPartnershipAds = false,
  appPageId = '',
}: UseAdSettings<TData>) => {
  const queryKey = isPartnershipAds ? queryKeys.partnershipAdSettings : queryKeys.adSettings;
  const normalizedAppPageId = appPageId.trim();
  const hasRequiredPartnershipAppPageId = !isPartnershipAds || Boolean(normalizedAppPageId);
  const isEnabledByConfig = config?.enabled ?? true;
  const requestBody: AdSettingsRequestPayload = isPartnershipAds
    ? { ...body, app_page_id: normalizedAppPageId }
    : body;

  return useQuery({
    ...config,
    staleTime: 0,
    enabled: isEnabledByConfig && hasRequiredPartnershipAppPageId,
    refetchOnMount: true,
    queryFn: () => getAdSettings(requestBody, queryKey.join('/') + '/'),
    queryKey: [
      ...queryKey,
      Object.values(requestBody).join(','),
    ],
    retry: false,
  });
};

export default useAdSettings;